import React, { createContext, useContext, useEffect, ReactNode } from "react";
import SignalRService from "../services/SignalRService";
import { API_TOKEN_KEY } from "constants/config";
import { Snackbar, Alert } from "@mui/material";

interface SignalRContextType {
  // Add any specific SignalR context properties if needed
}

const SignalRContext = createContext<SignalRContextType | null>(null);

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (context === undefined) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};

interface SignalRProviderProps {
  children: ReactNode;
  isAuthenticated: boolean;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
  isAuthenticated,
}) => {
  const [toastOpen, setToastOpen] = React.useState(false);

  useEffect(() => {
    const signalRService = SignalRService.getInstance();

    if (isAuthenticated) {
      const getToken = () => localStorage.getItem(API_TOKEN_KEY) || "";
      signalRService.startConnection(getToken).catch((err) => {
        console.error("Failed to start SignalR connection in Provider:", err);
      });

      // Override the service's internal stop logic or add a callback if needed
      // For now, we'll just monitor the retry count if we had access,
      // but since it's a singleton, let's add a simple event listener or check.
    } else {
      signalRService.stopConnection();
    }

    // Cleanup: strictly stop connection on unmount
    return () => {
      signalRService.stopConnection();
    };
  }, [isAuthenticated]);

  // Listen for a custom event from the service when it stops due to max retries
  useEffect(() => {
    const handleSignalRStopped = () => {
      setToastOpen(true);
    };

    window.addEventListener(
      "signalr_max_retries_reached",
      handleSignalRStopped,
    );
    return () => {
      window.removeEventListener(
        "signalr_max_retries_reached",
        handleSignalRStopped,
      );
    };
  }, []);

  return (
    <SignalRContext.Provider value={{}}>
      {children}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          SignalR connection failed after maximum attempts. Real-time updates
          are disabled.
        </Alert>
      </Snackbar>
    </SignalRContext.Provider>
  );
};
